import React from 'react'

import AdpStep from './adp-step'
import ChoosePlatformStep from './choose-platform-step'
import EmailStep from './email-step'
import MfaStep from './mfa-step'
import NoAccessStep from './no-access-step'
import PasswordStep from './password-step'
import PlatformStep from './platform-step'
import ResetCodeStep from './reset-code-step'
import ResetRequestStep from './reset-request-step'
import { DetectedPlatform, SigninStep } from '../types'

export interface ISigninStepHandlers {
  onSubmitEmail: (email: string) => void
  onSubmitPassword: (password: string) => void
  onSubmitAdp: (email: string) => void
  onSubmitMfaCode: (code: string) => void
  onSigninWithEmail: () => void
  onRequestResetCode: (email: string) => void
  onSubmitNewPassword: (params: { code: string; password: string }) => void
  onResendResetCode: () => void
  onBackToSignin: () => void
  onPlatformSignin: (platform: DetectedPlatform) => void
  onEmailChanged: (email: string) => void
  onBannerDismissed: () => void
  onChangeEmail: () => void
  onDifferentMethod: (platform: DetectedPlatform) => void
  onForgotPassword: () => void
  onSignUp: () => void
}

export interface ISigninStepProps {
  step: SigninStep
  isBusy: boolean
  handlers: ISigninStepHandlers
}

/** Picks the screen the lookup answer calls for. */
const SigninStepView = ({ step, isBusy, handlers }: ISigninStepProps): React.ReactElement => {
  switch (step.name) {
    case 'password':
      return (
        <PasswordStep
          email={step.email}
          adpEnabled={step.adpEnabled}
          isBusy={isBusy}
          onSubmit={handlers.onSubmitPassword}
          onPasswordChanged={handlers.onBannerDismissed}
          onChangeEmail={handlers.onChangeEmail}
          onForgotPassword={handlers.onForgotPassword}
          onConnectWithAdp={() => handlers.onSubmitAdp(step.email)}
        />
      )

    case 'platform':
      return (
        <PlatformStep
          platform={step.platform}
          source={step.source}
          email={step.email}
          organizationName={step.organizationName}
          isBusy={isBusy}
          onPlatformSignin={() => handlers.onPlatformSignin(step.platform)}
          onChangeEmail={handlers.onChangeEmail}
          onDifferentMethod={() => handlers.onDifferentMethod(step.platform)}
        />
      )

    case 'choosePlatform':
      return (
        <ChoosePlatformStep
          email={step.email}
          platforms={step.platforms}
          hasEmailAccount={Boolean(step.username)}
          isBusy={isBusy}
          onPlatformSignin={handlers.onPlatformSignin}
          onSigninWithEmail={handlers.onSigninWithEmail}
          onChangeEmail={handlers.onChangeEmail}
        />
      )

    case 'noAccess':
      return <NoAccessStep email={step.email} platform={step.platform} onChangeEmail={handlers.onChangeEmail} onDifferentMethod={() => handlers.onDifferentMethod(step.platform)} />

    case 'mfa':
      return <MfaStep isBusy={isBusy} onSubmit={handlers.onSubmitMfaCode} onCodeChanged={handlers.onBannerDismissed} onBackToSignin={handlers.onBackToSignin} />

    case 'resetRequest':
      return (
        <ResetRequestStep
          email={step.email}
          isBusy={isBusy}
          onSubmit={handlers.onRequestResetCode}
          onEmailChanged={handlers.onBannerDismissed}
          onBackToSignin={handlers.onBackToSignin}
        />
      )

    case 'resetCode':
      return (
        <ResetCodeStep
          isBusy={isBusy}
          onSubmit={handlers.onSubmitNewPassword}
          onCodeChanged={handlers.onBannerDismissed}
          onResendCode={handlers.onResendResetCode}
          onBackToSignin={handlers.onBackToSignin}
        />
      )

    case 'adp':
      return <AdpStep email={step.email} isBusy={isBusy} onSubmit={handlers.onSubmitAdp} onEmailChanged={handlers.onEmailChanged} />

    default:
      return <EmailStep email={step.email} isBusy={isBusy} onSubmit={handlers.onSubmitEmail} onEmailChanged={handlers.onEmailChanged} onSignUp={handlers.onSignUp} />
  }
}

export default SigninStepView
